import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { toast } from "sonner";
import { dict } from "../i18n/translations";
import api from "../lib/api";

const LanguageContext = createContext(null);
export const useLang = () => useContext(LanguageContext);

// Marker in localStorage: "sv" or "en" for explicit persistence, plus
// "ts_lang_autodetected" so we know whether the current value came from
// auto-detection (and should trigger the friendly toast once) or an explicit
// user choice (silent).
const LANG_KEY = "ts_lang";
const AUTODETECT_FLAG = "ts_lang_autodetected";

export const LanguageProvider = ({ children }) => {
  // Initial language must be deterministic for prerender/hydration parity:
  //   1. `/sv` prefix — the URL wins. Prerendered SV pages hydrate as Swedish.
  //   2. Persisted `ts_lang` — a returning visitor keeps their prior choice.
  //   3. `navigator.language` — auto-detect. Nordic locales (sv, sv-SE, sv-FI)
  //      default to Swedish; every other browser gets English so international
  //      users never accidentally hit Swedish copy.
  //   4. Fallback: English.
  const getInitialLang = () => {
    try {
      if (typeof window !== "undefined" && window.location.pathname.startsWith("/sv")) return "sv";
      const saved = localStorage.getItem(LANG_KEY);
      if (saved === "sv" || saved === "en") return saved;
      const nav = (typeof navigator !== "undefined" && navigator.language) || "";
      // `sv`, `sv-SE`, `sv-FI` → Swedish. Anything else → English default.
      const detected = nav.toLowerCase().startsWith("sv") ? "sv" : "en";
      // Persist so the toast only shows once, and remember it was auto-picked.
      try {
        localStorage.setItem(LANG_KEY, detected);
        localStorage.setItem(AUTODETECT_FLAG, "1");
      } catch { /* private mode */ }
      return detected;
    } catch { return "en"; }
  };
  const [lang, setLangState] = useState(getInitialLang);
  const [overrides, setOverrides] = useState({ en: {}, sv: {} });
  const [cms, setCms] = useState({});

  useEffect(() => {
    // Persist Swedish preference when the visitor arrives on an /sv page so signup
    // (which happens on non-/sv routes) can pick it up as the email language.
    try { if (window.location.pathname.startsWith("/sv")) localStorage.setItem(LANG_KEY, "sv"); } catch { /* noop */ }

    // First-load auto-detect toast: a quiet, one-time confirmation so people
    // know why the UI showed up in English (or Swedish). We use a top-level
    // toast because we can't call `useDashT()` here — but we can look the
    // English text up directly against the same dict. Fire once, then clear
    // the flag so future navigations stay silent.
    try {
      if (localStorage.getItem(AUTODETECT_FLAG) === "1") {
        localStorage.removeItem(AUTODETECT_FLAG);
        const isSv = lang === "sv";
        const message = isSv
          ? "Språket är satt till svenska. Du kan byta till EN när som helst."
          : "Language set to English — you can switch to SV anytime.";
        // 6s is long enough to read, short enough to feel un-nagging.
        setTimeout(() => toast(message, { duration: 6000, id: "lang-autodetect" }), 400);
      }
    } catch { /* noop */ }

    api.get("/i18n/overrides").then(({ data }) => setOverrides(data || { en: {}, sv: {} })).catch(() => {});
    api.get("/cms/blocks").then(({ data }) => setCms(data || {})).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setLang = useCallback((l) => {
    // Explicit user choice — no toast on next reload.
    try { localStorage.removeItem(AUTODETECT_FLAG); } catch { /* noop */ }
    localStorage.setItem(LANG_KEY, l);
    setLangState(l);
  }, []);

  const t = useCallback((key) => {
    // CMS content blocks take top priority. They can be stored per-language using a
    // `.sv` suffix (e.g. `home.hero.title.sv`), so Swedish visitors see Swedish copy
    // even when admins have customised the English default. Seeded CMS keys use a
    // `home.` prefix (home.hero.subtitle) while components call t('hero.subtitle'),
    // so the alias below makes those blocks actually render.
    const suffix = lang === "sv" ? ".sv" : "";
    if (suffix && cms[`${key}${suffix}`] != null) return cms[`${key}${suffix}`];
    if (suffix && cms[`home.${key}${suffix}`] != null) return cms[`home.${key}${suffix}`];
    if (cms[key] != null && lang !== "sv") return cms[key];
    if (cms[`home.${key}`] != null && lang !== "sv") return cms[`home.${key}`];
    const ov = overrides[lang]?.[key] ?? overrides.en?.[key];
    if (ov != null) return ov;
    const parts = key.split(".");
    let en = dict.en, sv = dict.sv;
    for (const p of parts) { en = en?.[p]; sv = sv?.[p]; }
    const dictVal = (lang === "sv" ? sv : en) ?? en;
    if (dictVal != null) return dictVal;
    // As a final fallback for lang=sv, use the English CMS block if defined
    // (better than showing the raw key).
    if (cms[key] != null) return cms[key];
    if (cms[`home.${key}`] != null) return cms[`home.${key}`];
    return key;
  }, [lang, overrides, cms]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, overrides }}>
      {children}
    </LanguageContext.Provider>
  );
};
